// src/ui/viewport.ts
//
// Static image viewport. The dithered result is rendered into a hidden
// <canvas> (still used by the Save button via `canvas.toBlob`) and mirrored
// into a visible <img> element on top. We display an <img> rather than a
// <canvas> for one specific reason: iOS Safari (and other mobile browsers)
// only show the native long-press menu — "Save to Photos / Share / Copy" —
// when the user long-presses an <img> tag. Long-pressing a <canvas> shows
// nothing useful. So we render to canvas (fast, sync) and update the <img>
// source from canvas.toBlob() on every frame.

export interface Viewport {
  el: HTMLElement;
  canvas: HTMLCanvasElement;
  setImage(data: ImageData): void;
  fitToView(): void;
  reset(): void;
  destroy(): void;
}

export function createViewport(): Viewport {
  const el = document.createElement('div');
  el.className = 'viewport';

  // The canvas is hidden — it exists only as a rendering buffer that Save
  // Image and Export Video can read pixels from.
  const canvas = document.createElement('canvas');
  canvas.style.display = 'none';
  el.append(canvas);

  // The visible image. iOS shows its native context menu when long-pressed.
  // Kept hidden until the first frame is rendered so browsers don't show a
  // placeholder / broken-image artifact while src is empty.
  const img = document.createElement('img');
  img.alt = '';
  img.draggable = false;
  img.className = 'viewport-img';
  img.style.visibility = 'hidden';
  el.append(img);

  let currentObjectURL: string | null = null;

  const updateImg = () => {
    canvas.toBlob(blob => {
      if (!blob) return;
      const next = URL.createObjectURL(blob);
      const prev = currentObjectURL;
      img.src = next;
      img.style.visibility = '';
      currentObjectURL = next;
      // Release the previous URL on next tick — gives the <img> time to load
      // the new src before the old blob is revoked.
      if (prev) requestAnimationFrame(() => URL.revokeObjectURL(prev));
    }, 'image/png');
  };

  const ctx = canvas.getContext('2d');
  return {
    el,
    canvas,
    setImage(data) {
      canvas.width = data.width;
      canvas.height = data.height;
      ctx?.putImageData(data, 0, 0);
      updateImg();
    },
    fitToView() {
      // The visible <img> is laid out by CSS (`object-fit: contain`), so
      // there's nothing for us to do here. This method is kept for API
      // compatibility with callers.
    },
    reset() {
      // Same: layout handles fitting.
    },
    destroy() {
      if (currentObjectURL) URL.revokeObjectURL(currentObjectURL);
      currentObjectURL = null;
    },
  };
}
